import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'

import { useLazyGetIntegrationUsersQuery, useSaveAIAgentResultMutation } from '../../../store/integrations/integrations'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'

import { useExportConfirmation } from '../../../components/ConfirmationDialog/useExportConfirmation'
import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Modal } from '../../../components/Modal/Modal'

import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../models/Account'

import { getContactOptions } from '../../MeetingSummary/CRM/utils'
import { useDocumentSummaryIntegrationExport } from './hooks'
import { selectProcessDocumentFileInfo } from '../../../store/processDocument/documentInfo'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { ContactDropdown } from '../../MeetingSummary/CRM/ContactDropdown'
import { IntegrationServiceId } from '../../../common/integrations'

const _DEFAULT_CONTACT_OBJECT = 'contact'

interface FormValues {
  externalUserId?: string
}

interface DocumentSummaryIntegrationExportModalProps {
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
}

export const DocumentSummaryIntegrationExportModal: React.FC<DocumentSummaryIntegrationExportModalProps> = ({ show, onHide, serviceId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const fileInfo = useAppSelector(selectProcessDocumentFileInfo)

  const [triggerSaveAIAgentResult, { isSuccess, isLoading }] = useSaveAIAgentResultMutation()

  const { trackingClient } = useTracking()

  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation()
  const { aiAgentResult } = useDocumentSummaryIntegrationExport()

  const { data: account } = useGetAccountQuery({ accountId })

  const contactObject = useMemo(() => {
    if (serviceId !== 'salesforce') {
      return null
    }

    if (!account?.data) {
      return _DEFAULT_CONTACT_OBJECT
    }

    const accountCRMIntegrationConfig = getAccountCRMIntegrationConfig(account.data, serviceId)
    if (!accountCRMIntegrationConfig) {
      return _DEFAULT_CONTACT_OBJECT
    }

    const contactMapping = accountCRMIntegrationConfig.mappings[AccountIntegrationConfigMappingType.CONTACT]
    return typeof contactMapping === 'string' ? contactMapping : (contactMapping?.objectName ?? _DEFAULT_CONTACT_OBJECT)
  }, [account, serviceId])

  const form = useForm<FormValues>()

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        addToast({
          variant: 'success',
          title: 'Document Summary saved',
          messages: ['Document Summary was successfully saved to your integration'],
        }),
      )

      trackingClient.track({ eventName: TrackEvent.DOCUMENT_SUMMARY_SAVED, eventDetails: { targetSystem: serviceId } })

      onHide()
      form.reset()
    }
  }, [dispatch, form, isSuccess, onHide, serviceId, trackingClient])

  const handleSave = async (data: FormValues) => {
    const { externalUserId } = data

    if (!externalUserId || !fileInfo || !aiAgentResult) {
      return
    }

    const { fileId } = fileInfo
    const { id: resultId } = aiAgentResult

    await runMutationWithConfirmation(async () => {
      await triggerSaveAIAgentResult({
        accountId,
        connector: serviceId,
        body: {
          relatedTo: { id: externalUserId, type: contactObject },
          resultId,
          fileId,
        },
      })
    })
  }

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={onHide}
        header={
          <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
            Save your document summary to&nbsp;<span className='text-capitalize'>{serviceId}</span>
          </BootstrapModal.Title>
        }
        body={
          <FormProvider {...form}>
            <DocumentSummaryIntegrationExportModalBody serviceId={serviceId} />
          </FormProvider>
        }
        onFooterPrimaryAction={form.handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading || accountIsLoading}
        footerPrimaryActionDisabled={!fileInfo || !aiAgentResult}
        onFooterSecondaryAction={onHide}
        showFooterActions={true}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

interface DocumentSummaryIntegrationExportModalBodyProps {
  serviceId: IntegrationServiceId
}

const DocumentSummaryIntegrationExportModalBody: React.FC<DocumentSummaryIntegrationExportModalBodyProps> = ({ serviceId }) => {
  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactSelector serviceId={serviceId} />
        </Col>
      </Row>
    </Container>
  )
}

interface ContactSelectorProps {
  serviceId: IntegrationServiceId
}

const ContactSelector: React.FC<ContactSelectorProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const [contactsLoading, setContactsLoading] = useState<boolean>(false)
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [searchInput, setSearchInput] = useState<string | null>(null)

  const [triggerGetContacts] = useLazyGetIntegrationUsersQuery()

  useEffect(() => {
    const fetchContacts = async (connector: string) => {
      setContactsLoading(true)
      try {
        const filters = searchInput ? { name: searchInput } : {}
        const { data: contactsResponse } = await triggerGetContacts({ accountId, connector, filters })
        if (!contactsResponse) {
          return
        }
        setContactOptions(getContactOptions(contactsResponse.data, connector))
      } catch (e) {
        console.error(e)
      } finally {
        setContactsLoading(false)
      }
    }

    fetchContacts(serviceId)
  }, [accountId, searchInput, serviceId, triggerGetContacts])

  const handleSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  return (
    <ContactDropdown
      fieldName='externalUserId'
      contactOptions={contactOptions}
      serviceId={serviceId}
      onSearchInputChange={handleSearchInputChange}
      isLoading={contactsLoading}
    />
  )
}
